import {
    defaultEstimatingFormState,
    type EstimatingFormState,
} from "../types";

export const ESTIMATING_ATTRIBUTE_NAMES = {
    wasteFactorPercent: "EST_WASTE_FACTOR",
    targetMarginPercent: "EST_TARGET_MARGIN",
    bidAmountOverride: "EST_BID_AMOUNT",
    billedAmount: "EST_BILLED_AMOUNT",
    shingleUpgradeNotes: "EST_SHINGLE_UPGRADE_NOTES",
    shingleUpgradeAmount: "EST_SHINGLE_UPGRADE_AMOUNT",
    projectNotes: "Special Project Notes",
    paymentType: "EST_PAYMENT_TYPE",
    paymentTerms: "EST_PAYMENT_TERMS",
    opPercent: "EST_OP_PERCENT",
    referralFee: "EST_REFERRAL_FEE",
    permitCost: "EST_PERMIT_COST",
    dumpsterFee: "EST_DUMPSTER_FEE",
    eagleviewCost: "EST_EAGLEVIEW_COST",
    jobPhotosFee: "EST_JOB_PHOTOS_FEE",
} as const;

type InspectionAttributeRecord = {
    Id?: string;
    AttributeName?: string;
    AttributeValue?: string | null;
};

function parseNumber(
    value: string | undefined,
    fallback: number,
): number {
    if (value === undefined || value === null || value === "") {
        return fallback;
    }
    const next = Number(value);
    return Number.isFinite(next) ? next : fallback;
}

function parseNullableNumber(value: string | undefined): number | null {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const next = Number(value);
    return Number.isFinite(next) ? next : null;
}

function getAttributeValue(
    attributes: InspectionAttributeRecord[],
    name: string,
): string | undefined {
    return attributes.find((attr) => attr.AttributeName === name)
        ?.AttributeValue ?? undefined;
}

export function parseEstimatingFromAttributes(
    attributes: InspectionAttributeRecord[],
): EstimatingFormState {
    const defaults = defaultEstimatingFormState;

    return {
        wasteFactorPercent: parseNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.wasteFactorPercent,
            ),
            defaults.wasteFactorPercent,
        ),
        targetMarginPercent: parseNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.targetMarginPercent,
            ),
            defaults.targetMarginPercent,
        ),
        bidAmountOverride: parseNullableNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.bidAmountOverride,
            ),
        ),
        billedAmount: parseNullableNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.billedAmount,
            ),
        ),
        shingleUpgradeNotes:
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.shingleUpgradeNotes,
            ) ?? defaults.shingleUpgradeNotes,
        shingleUpgradeAmount: parseNullableNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.shingleUpgradeAmount,
            ),
        ),
        projectNotes:
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.projectNotes,
            ) ?? defaults.projectNotes,
        paymentType:
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.paymentType,
            ) || defaults.paymentType,
        paymentTerms:
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.paymentTerms,
            ) || defaults.paymentTerms,
        opPercent: parseNumber(
            getAttributeValue(attributes, ESTIMATING_ATTRIBUTE_NAMES.opPercent),
            defaults.opPercent,
        ),
        referralFee: parseNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.referralFee,
            ),
            defaults.referralFee,
        ),
        permitCost: parseNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.permitCost,
            ),
            defaults.permitCost,
        ),
        dumpsterFee: parseNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.dumpsterFee,
            ),
            defaults.dumpsterFee,
        ),
        eagleviewCost: parseNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.eagleviewCost,
            ),
            defaults.eagleviewCost,
        ),
        jobPhotosFee: parseNumber(
            getAttributeValue(
                attributes,
                ESTIMATING_ATTRIBUTE_NAMES.jobPhotosFee,
            ),
            defaults.jobPhotosFee,
        ),
    };
}

export function buildEstimatingAttributeIds(
    attributes: InspectionAttributeRecord[],
): Record<string, string> {
    const ids: Record<string, string> = {};
    const relevantNames = new Set<string>(
        Object.values(ESTIMATING_ATTRIBUTE_NAMES),
    );

    for (const attr of attributes) {
        if (!attr.Id || !attr.AttributeName) continue;
        if (!relevantNames.has(attr.AttributeName)) continue;
        ids[attr.AttributeName] = attr.Id;
    }

    return ids;
}

export type EstimatingFormValues = Omit<
    EstimatingFormState,
    "bidAmountOverride" | "billedAmount" | "shingleUpgradeAmount"
> & {
    bidAmountOverride: number | "" | null;
    billedAmount: number | "" | null;
    shingleUpgradeAmount: number | "" | null;
};

export function toEstimatingFormValues(
    state: EstimatingFormState,
): EstimatingFormValues {
    return {
        ...state,
        bidAmountOverride: state.bidAmountOverride ?? "",
        billedAmount: state.billedAmount ?? "",
        shingleUpgradeAmount: state.shingleUpgradeAmount ?? "",
    };
}

export function fromEstimatingFormValues(
    form: EstimatingFormValues,
): EstimatingFormState {
    const parseNullable = (
        value: number | "" | null | undefined,
    ): number | null => {
        if (value === "" || value === null || value === undefined) {
            return null;
        }
        const next = Number(value);
        return Number.isFinite(next) ? next : null;
    };

    return {
        ...form,
        wasteFactorPercent: parseNumber(String(form.wasteFactorPercent), 10),
        targetMarginPercent: parseNumber(
            String(form.targetMarginPercent),
            25,
        ),
        bidAmountOverride: parseNullable(form.bidAmountOverride),
        billedAmount: parseNullable(form.billedAmount),
        shingleUpgradeAmount: parseNullable(form.shingleUpgradeAmount),
        opPercent: parseNumber(String(form.opPercent), 20),
        referralFee: parseNumber(String(form.referralFee), 0),
        permitCost: parseNumber(String(form.permitCost), 0),
        dumpsterFee: parseNumber(String(form.dumpsterFee), 0),
        eagleviewCost: parseNumber(String(form.eagleviewCost), 0),
        jobPhotosFee: parseNumber(String(form.jobPhotosFee), 0),
    };
}

export type EstimatingFieldKey = keyof EstimatingFormState;

function isEstimatingFieldEqual(
    field: EstimatingFieldKey,
    current: EstimatingFormState,
    saved: EstimatingFormState,
) {
    const left = current[field];
    const right = saved[field];

    if (left === right) return true;
    if (left == null && right == null) return true;
    if (typeof left === "number" && typeof right === "number") {
        return left === right;
    }

    return String(left ?? "") === String(right ?? "");
}

export function hasEstimatingChanges(
    current: EstimatingFormState,
    saved: EstimatingFormState,
): boolean {
    return (Object.keys(ESTIMATING_ATTRIBUTE_NAMES) as EstimatingFieldKey[]).some(
        (field) => !isEstimatingFieldEqual(field, current, saved),
    );
}

export function serializeEstimatingAttributeValue(
    field: EstimatingFieldKey,
    state: EstimatingFormState,
): string {
    const value = state[field];

    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "number") {
        return String(value);
    }

    return String(value);
}

export type EstimatingAttributeChange = {
    field: EstimatingFieldKey;
    attributeName: string;
    value: string;
};

export function identifyEstimatingAttributeChanges(
    current: EstimatingFormState,
    saved: EstimatingFormState,
): EstimatingAttributeChange[] {
    const changes: EstimatingAttributeChange[] = [];

    for (const field of Object.keys(
        ESTIMATING_ATTRIBUTE_NAMES,
    ) as EstimatingFieldKey[]) {
        if (isEstimatingFieldEqual(field, current, saved)) continue;

        changes.push({
            field,
            attributeName: ESTIMATING_ATTRIBUTE_NAMES[field],
            value: serializeEstimatingAttributeValue(field, current),
        });
    }

    return changes;
}
